import {
	CampaignConfirmResult,
	CampaignRoutingSave,
	CampaignRoutingSettings,
	campaignConfirmResultSchema,
	campaignRoutingSettingsSchema,
} from "@signalist/shared-types";

import { api } from "@/redux/api";

import type { BulkStatusScope } from "@/pages/Private/pages/List/components/UnassignedTable/utils/getBulkStatusScope";

/**
 * `POST list/campaign-routing/confirm` (PREC-4219): the selection, as every bulk action sends it.
 * No campaign: each lead is enrolled into the campaign it already holds.
 */
export type ConfirmPreassignedRequest = {
	listId: number;
	assign: { listPersonId: number }[];
} & Partial<BulkStatusScope>;

/**
 * A list's campaign routing (PREC-4219): the ordered rules, the no-match fallback, and the bulk
 * "Confirm preassigned campaigns". The bodies and answers are the shared-types contract the API
 * validates (`campaign-routing.ts`), so a field one side knows and the other does not is a type error.
 */
export const campaignRoutingApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCampaignRouting: builder.query<CampaignRoutingSettings, number>({
			query: (listId) => ({
				url: `list/${listId}/campaign-routing`,
				responseSchema: campaignRoutingSettingsSchema,
			}),
			// The answer also judges the list's campaigns (each target's state, which campaign is the
			// default), so it provides the list's tag too: every mutation that moves the default,
			// deletes a campaign or changes its status or content already invalidates that tag, and the
			// routing refetches with it instead of naming yesterday's default.
			providesTags: (result, error, listId) => [
				{ type: "CampaignRouting", id: listId },
				{ type: "List", id: listId },
			],
		}),
		saveCampaignRouting: builder.mutation<
			CampaignRoutingSettings,
			{ listId: number; body: CampaignRoutingSave }
		>({
			query: ({ listId, body }) => ({
				url: `list/${listId}/campaign-routing`,
				method: "PUT",
				body,
				responseSchema: campaignRoutingSettingsSchema,
			}),
			// A save reroutes the list's leads that hold no explicit choice, so the table refetches.
			// Only on success: a refused save leaves the editor open for a retry (web §24e).
			invalidatesTags: (result, error, { listId }) =>
				error ? [] : [{ type: "CampaignRouting", id: listId }, "ListPerson"],
		}),
		confirmPreassignedCampaigns: builder.mutation<CampaignConfirmResult, ConfirmPreassignedRequest>(
			{
				query: (body) => ({
					url: "list/campaign-routing/confirm",
					method: "POST",
					body,
					responseSchema: campaignConfirmResultSchema,
				}),
				// The same tags as bulkAssign: the leads moved status and joined campaigns.
				invalidatesTags: (result, error, { listId }) =>
					error ? [] : [{ type: "List", id: listId }, "ListPerson", "Campaign"],
			}
		),
	}),
});

export const {
	useGetCampaignRoutingQuery,
	useSaveCampaignRoutingMutation,
	useConfirmPreassignedCampaignsMutation,
} = campaignRoutingApi;
